import { useNavigate } from "react-router-dom";
import { useState, useEffect } from 'react';
import bhcalc from '../bhcalc.js';

function AddClient({ client, onSaved }) {
  const navigate = useNavigate();
  const isEdit = !!client;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [locationPrograms, setLocationPrograms] = useState([]);
  const [formData, setFormData] = useState({
    firstName: client?.firstName ?? '',
    lastName: client?.lastName ?? '',
    email: client?.email ?? '',
    gender: client?.gender ?? '',
    age: client?.age ?? '',
    heightFeet: client?.heightFeet ?? '',
    heightInches: client?.heightInches ?? '',
    weight: client?.weight ?? '',
    abdomenInches: client?.abdomenInches ?? '',
    bicepsInches: client?.bicepsInches ?? '',
    thighInches: client?.thighInches ?? '',
    calfInches: client?.calfInches ?? '',
    wristInches: client?.wristInches ?? '',
    hipDiameterInches: client?.hipDiameterInches ?? '',
    cardioDays: client?.cardioDays ?? '',
    cardioMinutes: client?.cardioMinutes ?? '',
    strengthDays: client?.strengthDays ?? '',
    strengthMinutes: client?.strengthMinutes ?? '',
    mealsPerDay: client?.mealsPerDay ?? '',
    snacksPerDay: client?.snacksPerDay ?? '',
    timeOfFirstMeal: client?.timeOfFirstMeal ?? '',
    timeOfLastMeal: client?.timeOfLastMeal ?? '',
    isDiabetic: client?.isDiabetic ?? false,
    hasPrediabetes: client?.hasPrediabetes ?? false,
    hasDiabetes1: client?.hasDiabetes1 ?? false,
    hasDiabetes2: client?.hasDiabetes2 ?? false,
    hadBariatricSurgery: client?.hadBariatricSurgery ?? false,
    typeOfBariatric: client?.typeOfBariatric ?? '',
    isLactating: client?.isLactating ?? false,
    notes: client?.notes ?? '',
    coachId: client?.coachId ?? '',
    locationProgramId: client?.locationPrograms?.[0]?.id ?? '',
  });

  useEffect(() => {
    bhcalc.get('/coach').then(({ data }) => setCoaches(data)).catch(() => {});
    bhcalc.get('/location-program').then(({ data }) => {
      setLocationPrograms(data.filter(p => !p.isArchived));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        coachId: formData.coachId ? Number(formData.coachId) : undefined,
        locationProgramIds: formData.locationProgramId ? [Number(formData.locationProgramId)] : undefined,
        locationProgramId: undefined,
      };
      if (isEdit) {
        await bhcalc.put(`/partner-client/${client.id}`, payload);
      } else {
        await bhcalc.post('/partner-client', payload);
      }
      if (onSaved) {
        onSaved();
      } else {
        navigate('/clients');
      }
    } catch {
      setError(`Failed to ${isEdit ? 'update' : 'add'} client. Please try again.`);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onSaved) {
      onSaved();
    } else {
      navigate('/clients');
    }
  };

  return (
    <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Client' : 'Add New Client'}</h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Top Section - Single Column */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Personal Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter First Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter Last Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coach
                  </label>
                  <select
                    name="coachId"
                    value={formData.coachId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Coach</option>
                    {coaches.map(coach => (
                      <option key={coach.id} value={coach.id}>
                        {coach.firstName} {coach.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Program
                  </label>
                  <select
                    name="locationProgramId"
                    value={formData.locationProgramId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Program</option>
                    {locationPrograms.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.programName}
                      </option>
                    ))}
                  </select>
                </div>

              </div>
            </div>

            {/* Middle Section - Two Columns */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Health Details</h3>
              <div className="space-y-4">

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Age"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height Feet
                    </label>
                    <input
                      type="number"
                      name="heightFeet"
                      value={formData.heightFeet}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height Inches
                    </label>
                    <input
                      type="number"
                      name="heightInches"
                      value={formData.heightInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weight in Pounds
                    </label>
                    <input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Abdomen Measurement in Inches
                    </label>
                    <input
                      type="number"
                      name="abdomenInches"
                      value={formData.abdomenInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dominant Biceps in Inches
                    </label>
                    <input
                      type="number"
                      name="bicepsInches"
                      value={formData.bicepsInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dominant Thigh in Inches
                    </label>
                    <input
                      type="number"
                      name="thighInches"
                      value={formData.thighInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dominant Calf in Inches
                    </label>
                    <input
                      type="number"
                      name="calfInches"
                      value={formData.calfInches}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="11"
                    />
                  </div>

                  {formData.gender === 'male' && (
                    <div id="male-only">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dominant Wrist Diameter in Inches
                      </label>
                      <input
                        type="number"
                        name="wristInches"
                        value={formData.wristInches}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="9"
                      />
                    </div>
                  )}

                  {formData.gender === 'female' && (
                    <div id="male-only">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hip Diameter in Inches
                      </label>
                      <input
                        type="number"
                        name="hipDiameterInches"
                        value={formData.hipDiameterInches}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="38"
                      />
                    </div>
                  )}

                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Activity</h3>
              <div className="space-y-4">

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cardio Sessions per Week
                    </label>
                    <input
                      type="number"
                      name="cardioDays"
                      value={formData.cardioDays}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cardio Duration in Minutes per Session
                    </label>
                    <input
                      type="number"
                      name="cardioMinutes"
                      value={formData.cardioMinutes}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Strength Training Sessions per Week
                    </label>
                    <input
                      type="number"
                      name="strengthDays"
                      value={formData.strengthDays}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Strength Training Duration in Minutes per Sessions
                    </label>
                    <input
                      type="number"
                      name="strengthMinutes"
                      value={formData.strengthMinutes}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="120"
                    />
                  </div>


              </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Diet</h3>
              <div className="space-y-4">

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Meals per Day
                    </label>
                    <input
                      type="number"
                      name="mealsPerDay"
                      value={formData.mealsPerDay}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Snacks per Day
                    </label>
                    <input
                      type="number"
                      name="snacksPerDay"
                      value={formData.snacksPerDay}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Average Time of First Meal (24-hr Clock)
                    </label>
                    <input
                      type="text"
                      name="timeOfFirstMeal"
                      value={formData.timeOfFirstMeal}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0830"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Average Time of Last Meal (24-hr Clock)
                    </label>
                    <input
                      type="text"
                      name="timeOfLastMeal"
                      value={formData.timeOfLastMeal}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="1830"
                    />
                  </div>

              </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Conditions</h3>
              <div className="space-y-4">

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="isDiabetic"
                        checked={formData.isDiabetic || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Is Client Pre-Diabetic or Diabetic?</span>
                    </label>
                  </div>

                  {formData.isDiabetic && (
                  <div className="col-span-2 ml-4">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="hasPrediabetes"
                        checked={formData.hasPrediabetes || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Prediabetes</span>
                    </label>
                  </div>
                  )}

                  {formData.isDiabetic && (

                  <div className="col-span-2 ml-4">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="hasDiabetes1"
                        checked={formData.hasDiabetes1 || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Diabetes Type 1</span>
                    </label>
                  </div>
                  )}

                  {formData.isDiabetic && (
                  <div className="col-span-2 ml-4">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="hasDiabetes2"
                        checked={formData.hasDiabetes2 || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Diabetes Type 2</span>
                    </label>
                  </div>
                  )}

                  <div className="col-span-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="hadBariatricSurgery"
                        checked={formData.hadBariatricSurgery || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Has Client had Bariatric Surgery?</span>
                    </label>
                  </div>

                  {formData.hadBariatricSurgery && (
                  <div className="col-span-2 ml-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type of Bariatric Surgery
                    </label>
                    <input
                      type="text"
                      name="typeOfBariatric"
                      value={formData.TypeOfBariatric}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Lap-band, Balloon, Gatric Bypass, Other"
                    />
                  </div>
                  )}


                {formData.gender === 'female' && (
                  <div className="col-span-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="isLactating"
                        checked={formData.isLactating || false}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span>Is Client Lactating?</span>
                    </label>
                  </div>
                )}

              </div>
              </div>
            </div>

            {/* Bottom Section - Single Column */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Additional</h3>
              <div className="space-y-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any additional information..."
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
              >
                {submitting ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Client')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddClient;
